import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./AddNewClientForm.scss";
import {
  FEMALE,
  GENDER_OTHER,
  MALE,
  NON_BINARY,
  PREFER_NOT_TO_SAY,
} from "../../Utilities/constants";
import {
  isEmailValid,
  validateClientAndCoClient,
} from "../../Utilities/validation";
import {
  formatBEDateStringToFE,
  formatFEDateStringToBE,
  formatTimestampToDate,
} from "../../Utilities/formatter";
import FormControl from "@mui/material/FormControl";
import states_list from "../../Data/us_states.json";
import country_list from "../../Data/country_list.json";
import ClientStatus from "../../Data/client_status.json";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import TextBox from "../../UX/TextBox/TextBox";
import NumericalTextBox from "../../UX/NumericalTextBox/NumericalTextBox";
import Button from "../../UX/Button/Button";
import DropDownList from "../../UX/DropDownList/DropDownList";
import MaskedTextBox from "../../UX/MaskedTextBox/MaskedTextBox";
import NativeDatePicker from "../../UX/NativeDatePicker/NativeDatePicker";
import {Paper} from "@mui/material";
import { styled } from '@mui/material/styles';

export const ImprovedSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: theme.palette.primary.main,
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color:
          theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const AddNewClientForm = (props) => {
  const { t } = useTranslation();

  const GENDER_DDL = [
    MALE,
    FEMALE,
    NON_BINARY,
    GENDER_OTHER,
    PREFER_NOT_TO_SAY,
  ];

  let defaultRetirementAge = props.defaultRetirementAge
    ? props.defaultRetirementAge
    : 65;
  let defaultLifeExpectancyAge = props.defaultLifeExpectancyAge
    ? props.defaultLifeExpectancyAge
    : 95;

  useEffect(() => {
    let newState = { ...state };
    newState.client = getMemberTemplate(props.client);
    newState.coClient = getMemberTemplate(props.coClient);
    setState(newState);
  }, []);

  const getMemberTemplate = (member) => {
    if (member == null || member.personId == null)
      return getNewCoClientTemplate();

    let templateMember = {
      firstName: {
        value: member ? member.firstName : "",
        isValid: true,
      },
      lastName: {
        value: member ? member.lastName : "",
        isValid: true,
      },
      middleName: {
        value: member ? member.middleName : "",
        isValid: true,
      },
      gender: {
        value: member ? member.gender : MALE,
        isValid: true,
      },
      genderOther: {
        value: member ? member.genderOther : "",
        isValid: true,
      },
      dateOfBirth: {
        value: member ? formatBEDateStringToFE(member.dateOfBirth) : null,
        isValid: true,
      },
      status: {
        value: member && member.status ? member.status : ClientStatus.status[0],
        isValid: true,
      },
      retirementAge: {
        value: member ? member.retirementAge : defaultRetirementAge,
        isValid: true,
      },
      lifeExpectancy: {
        value: member ? member.lifeExpectancyAge : defaultLifeExpectancyAge,
        isValid: true,
      },
      emailAddress:
        member && member.emailList ? member.emailList[0].emailAddress : "",
      phoneNumber:
        member && member.phoneList ? member.phoneList[0].phoneNumber : "",
      addressLine:
        member && member.addressList ? member.addressList[0].addressLine : "",
      city: member && member.addressList ? member.addressList[0].city : "",
      zipCode:
        member && member.addressList ? member.addressList[0].zipCode : "",
      state:
        member && member.addressList
          ? member.addressList[0].state
          : {
              name: "",
              "alpha-2": "",
            },
      country:
        member && member.addressList
          ? member.addressList[0].country
          : {
              id: "USA",
              name: "United States",
            },
    };
    if (member.relationshipType.id === "CLIENT") {
      templateMember.hasHealthInsurance = {
        value: member.hasHealthInsurance ? member.hasHealthInsurance : false,
        isValid: true,
      };
      templateMember.hasDisabilityInsurance = {
        value: member.hasDisabilityInsurance
          ? member.hasDisabilityInsurance
          : false,
        isValid: true,
      };
      templateMember.hasWill = {
        value: member.hasWill ? member.hasWill : false,
        isValid: true,
      };
      templateMember.hasTrust = {
        value: member.hasTrust ? member.hasTrust : false,
        isValid: true,
      };
      templateMember.isBeneficiariesUpToDate = {
        value: member.isBeneficiariesUpToDate
          ? member.isBeneficiariesUpToDate
          : false,
        isValid: true,
      };
    }
    return templateMember;
  };

  const getNewCoClientTemplate = () => {

    // Client’s Country, Address, City, State, and Zip data elements shall be auto populated onto the Co-Client page.
    const defaults = props.defaults ? props.defaults : null;


    return {
      firstName: {
        value: "",
        isValid: true,
      },
      lastName: {
        value: "",
        isValid: true,
      },
      middleName: {
        value: "",
        isValid: true,
      },
      gender: {
        value: MALE,
        isValid: true,
      },
      genderOther: {
        value: "",
        isValid: true,
      },
      dateOfBirth: {
        value: null,
        isValid: true,
      },
      status: {
        value: ClientStatus.status[0],
        isValid: true,
      },
      retirementAge: {
        value: defaultRetirementAge,
        isValid: true,
      },
      lifeExpectancy: {
        value: defaultLifeExpectancyAge,
        isValid: true,
      },
      isEligibleSocialSecuritySpousalBenefit: false,
      emailAddress: "",
      phoneNumber: "",
      addressLine: defaults ? defaults.addressLine : "",
      city: defaults ? defaults.city : "",
      zipCode: defaults ? defaults.zipCode : "",
      state: defaults ? defaults.state : {
        name: "",
        "alpha-2": "",
      },
      country: defaults ? defaults.country : {
        id: "USA",
        name: "United States",
      },
      hasHealthInsurance: {
        value: false,
        isValid: true,
      },
      hasDisabilityInsurance: {
        value: false,
        isValid: true,
      },
      hasWill: {
        value: false,
        isValid: true,
      },
      hasTrust: {
        value: false,
        isValid: true,
      },
      isBeneficiariesUpToDate: {
        value: false,
        isValid: true,
      },
      isNew: true,
    };
  };

  const [state, setState] = useState({
    client: getMemberTemplate(props.client),
    coClient: getMemberTemplate(props.coClient),
    needCoClient: props.client != null ? true : false,
  });

  let openedDropdownListRef = null;
  const setOpenedDropdownList = (ref) => {
    openedDropdownListRef = ref;
  };

  const hideDropdownOptions = (event) => {
    if (
      event.target.offsetHeight > 250 &&
      openedDropdownListRef &&
      openedDropdownListRef.state.opened
    ) {
      openedDropdownListRef.setState({
        focused: false,
        opened: false,
      });
    }
  };

  const update = (primaryKey, subKey, value) => {
    let newState = { ...state };
    newState[primaryKey].isNew = false;
    newState[primaryKey][subKey].value = value;
    newState[primaryKey][subKey].isValid = true;
    setState(newState);
  };

  const updateIsEligibleSocialSecuritySpousalBenefit = (
    primaryKey,
    subKey,
    value
  ) => {
    let newState = { ...state };
    newState[primaryKey].isNew = false;
    newState[primaryKey][subKey] = value;
    if (value) {
      update(primaryKey, "retirementAge", 62);
    }
    setState(newState);
  };

  const updateContact = (primaryKey, subKey, value) => {
    let newState = { ...state };
    newState[primaryKey].isNew = false;
    newState[primaryKey][subKey] = value;
    if (subKey === "country") {
      if (value.id === "USA") {
        newState[primaryKey]["state"] = { name: "", "alpha-2": "" };
      } else {
        newState[primaryKey]["state"] = "";
      }
    }
    setState(newState);
  };

  const updateDateOfBirth = (primaryKey, subKey) => {
    return (date) => {
      // make sure we are updating with a valid date and not the keyboard event.
      if (date && date.target) {
        return;
      }

      let newState = { ...state };
      newState[primaryKey].isNew = false;
      newState[primaryKey][subKey].value = date === "" ? null : date;
      newState[primaryKey][subKey].isValid = true;
      setState(newState);
    };
  };

  const generateNewClientParams = () => {
    if (props.client != null && props.client.id != null) {
      let client = { ...props.client };
      client.clientId = props.client.id;
      client.firstName = state.client.firstName.value;
      client.middleName = state.client.middleName.value;
      client.lastName = state.client.lastName.value;
      client.dateOfBirth = state.client.dateOfBirth.value
        ? formatFEDateStringToBE(state.client.dateOfBirth.value)
        : null;
      client.gender = state.client.gender.value;
      client.genderOther = state.client.genderOther.value;
      client.status = state.client.status.value;
      client.retirementAge = state.client.retirementAge.value;
      client.lifeExpectancyAge = state.client.lifeExpectancy.value;
      client.addressList = client.addressList.map((address) => {
        return {
          addressId: address.addressId,
          country: address.country.id,
          addressLine: address.addressLine,
          city: address.city,
          zipCode: address.zipCode,
          state:
            address.country.id === "USA"
              ? address.state["alpha-2"]
              : address.state,
        };
      });
      client.emailList[0] = {
        emailAddressId: client.emailList[0].emailAddressId,
        emailAddressType: "HOME",
        emailAddress: state.client.emailAddress,
      };
      client.phoneList[0] = {
        phoneNumberId: client.phoneList[0].phoneNumberId,
        phoneNumberType: "PERSONAL",
        phoneNumber: state.client.phoneNumber,
      };
      client.hasHealthInsurance = state.client.hasHealthInsurance
        ? state.client.hasHealthInsurance.value
        : false;
      client.hasDisabilityInsurance = state.client.hasDisabilityInsurance
        ? state.client.hasDisabilityInsurance.value
        : false;
      client.hasWill = state.client.hasWill
        ? state.client.hasWill.value
        : false;
      client.hasTrust = state.client.hasTrust
        ? state.client.hasTrust.value
        : false;
      client.isBeneficiariesUpToDate = state.client.isBeneficiariesUpToDate
        ? state.client.isBeneficiariesUpToDate.value
        : false;
      return client;
    } else {
      return {
        clientId: null,
        personId: null,
        firstName: state.client.firstName.value,
        middleName: state.client.middleName.value,
        lastName: state.client.lastName.value,
        salutation: null,
        dateOfBirth: state.client.dateOfBirth.value
          ? formatFEDateStringToBE(state.client.dateOfBirth.value)
          : null,
        gender: state.client.gender.value,
        genderOther: state.client.genderOther.value,
        company: {
          companyId: null,
          name: "",
          website: "",
        },
        notes: null,
        retirementAge: state.client.retirementAge.value,
        retirementMonth: "Jan",
        lifeExpectancyAge: state.client.lifeExpectancy.value,
        lifeExpectancyMonth: "Jan",
        status: state.client.status.value,
        relationshipType: "CLIENT",
        addressList: [
          {
            addressId: null,
            country: state.client.country.id,
            addressLine: state.client.addressLine,
            city: state.client.city,
            zipCode: state.client.zipCode,
            state:
              state.client.country.id === "USA"
                ? state.client.state["alpha-2"]
                : state.client.state,
          },
        ],
        emailList: [
          {
            emailAddressId: null,
            emailAddressType: "HOME",
            emailAddress: state.client.emailAddress,
          },
        ],
        phoneList: [
          {
            phoneNumberId: null,
            phoneNumberType: "PERSONAL",
            phoneNumber: state.client.phoneNumber,
          },
        ],
        hasHealthInsurance: state.client.hasHealthInsurance
          ? state.client.hasHealthInsurance.value
          : false,
        hasDisabilityInsurance: state.client.hasDisabilityInsurance
          ? state.client.hasDisabilityInsurance.value
          : false,
        hasWill: state.client.hasWill ? state.client.hasWill.value : false,
        hasTrust: state.client.hasTrust ? state.client.hasTrust.value : false,
        isBeneficiariesUpToDate: state.client.isBeneficiariesUpToDate
          ? state.client.isBeneficiariesUpToDate.value
          : false,
      };
    }
  };

  const save = () => {
    if (validate()) {
      const clientData = generateNewClientParams();
      props.save(clientData);
    }
  };

  const validate = () => {
    let newState = { ...state };
    newState["client"].isNew = false;
    newState["client"] = validateClientAndCoClient(newState["client"]);
    let isValid =
      newState["client"]["firstName"].isValid &&
      newState["client"]["lastName"].isValid &&
      newState["client"]["dateOfBirth"].isValid &&
      newState["client"]["retirementAge"].isValid &&
      newState["client"]["lifeExpectancy"].isValid &&
      isEmailValid(newState["client"].emailAddress) &&
      (newState["client"]["country"]["id"] === "USA"
        ? newState["client"]["state"]["alpha-2"] !== ""
        : newState["client"]["state"] !== "");
    setState(newState);

    return isValid;
  };

  const setupStateComponent = (type) => {
    let clientStateField;
    if (state[type].country.id === "USA") {
      clientStateField = (
        <FormControl
          margin="normal"
          required
          className="percent-95-width state-dropdown"
        >
          <Typography component={"label"}>State</Typography>
          <DropDownList
            placeholder={t("Global.state")}
            value={state[type].state}
            onChange={(event) =>
              updateContact(type, "state", event.target.value)
            }
            data={states_list}
            dataItemKey={"alpha-2"}
            textField={"name"}
            valid={state[type].state["alpha-2"] !== "" || state[type].isNew}
            reference={setOpenedDropdownList}
          />
        </FormControl>
      );
    } else {
      clientStateField = (
        <FormControl
          margin="normal"
          required
          className="percent-95-width state-textbox"
        >
          <Typography component={"label"}>State</Typography>
          <TextBox placeholder={t("Global.state")}
            minLength={0}
            maxLength={30}
            value={state[type].state}
            onChange={(event) =>
              updateContact(type, "state", event.target.value)
            }
            hasError={!(state[type].state !== "" || state[type].isNew)}
          />
        </FormControl>
      );
    }

    return clientStateField;
  };

  const setupContactInfoComponent = (type) => {
    let contactComponents = (
      <React.Fragment>
        {!props.hideContactInformation && (
          <React.Fragment>
            <Grid item xs={12} style={{ marginTop: "20px" }}>
              <Typography
                component={"p"}
                variant={"body1"}
                color={"primary"}
                style={{ cursor: "pointer" }}
                className={"client-form-section-header"}
              >
                {t("Clients.contact_info_optional_fields")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <TextBox
                  placeholder={t("Global.email")}
                  minLength={0}
                  maxLength={50}
                  value={state[type].emailAddress}
                  onChange={(event) =>
                    updateContact(type, "emailAddress", event.target.value)
                  }
                  validate={(value) => {
                    if (!isEmailValid(value)) return "Email is not valid";
                  }}
                  hasError={
                    !(
                      isEmailValid(state[type].emailAddress) ||
                      state[type].isNew
                    )
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <MaskedTextBox
                  placeholder={t("Global.phone")}
                  mask={"999-000-0000"}
                  value={state[type].phoneNumber}
                  onChange={(event) =>
                    updateContact(type, "phoneNumber", event.target.value)
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} className="address-divider" />
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <TextBox
                  placeholder={t("Global.address")}
                  minLength={0}
                  maxLength={30}
                  value={state[type].addressLine}
                  onChange={(event) =>
                    updateContact(type, "addressLine", event.target.value)
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <TextBox
                  placeholder={t("Global.city")}
                  minLength={0}
                  maxLength={30}
                  value={state[type].city}
                  onChange={(event) =>
                    updateContact(type, "city", event.target.value)
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              {setupStateComponent(type)}
            </Grid>
            <Grid item xs={4}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <Typography component={"label"}>Zip Code</Typography>
                <TextBox
                  placeholder={t("Global.zip_code")}
                  minLength={0}
                  maxLength={10}
                  value={state[type].zipCode}
                  onChange={(event) =>
                    updateContact(type, "zipCode", event.target.value)
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl
                margin="normal"
                required
                className="percent-95-width"
              >
                <Typography component={"label"}>Country</Typography>
                <DropDownList
                  placeholder={t("Global.country")}
                  value={state[type].country}
                  onChange={(event) =>
                    updateContact(type, "country", event.target.value)
                  }
                  data={country_list}
                  dataItemKey={"id"}
                  textField={"name"}
                  reference={setOpenedDropdownList}
                />
              </FormControl>
            </Grid>
          </React.Fragment>
        )}
      </React.Fragment>
    );

    return contactComponents;
  };

  const modalTitle = props.isCoClient
    ? t("Clients.add_new_coclient")
    : t("Clients.add_new_client");

  let buttonsGroup = (
    <React.Fragment>
      <span style={{ marginLeft: "10px" }}>
        <Button label={props.saveLabel ? props.saveLabel : "Save"} primary={true} outline={true} onClick={save} />
      </span>
    </React.Fragment>
  );

  let clientFirstNameComponent;
  if (props.client) {
    clientFirstNameComponent = (
      <TextBox
        placeholder={t("Clients.first_name")}
        minLength={0}
        maxLength={30}
        value={state.client.firstName.value}
        onChange={(event) => update("client", "firstName", event.target.value)}
        validate={(value) => {
          if (value === "") return "First Name can't be empty.";
        }}
        hasError={!state.client.firstName.isValid}
      />
    );
  } else {
    clientFirstNameComponent = (
      <TextBox
        placeholder={t("Clients.first_name")}
        minLength={0}
        maxLength={30}
        value={state.client.firstName.value}
        onChange={(event) => update("client", "firstName", event.target.value)}
        validate={(value) => {
          if (value === "") return "First Name can't be empty.";
        }}
        hasError={!state.client.firstName.isValid}
        autoFocus
      />
    );
  }

  let questionAnswerContent = (
    <React.Fragment>
      <Grid item xs={12} style={{ marginTop: "20px" }}>
        <Typography
          component={"p"}
          variant={"body1"}
          color={"primary"}
          style={{ cursor: "pointer" }}
          className={"client-form-section-header"}
        >
          {t("Clients.profile_questions")}
        </Typography>
      </Grid>
      <Grid item xs={3} className="question">
        <FormControl margin="normal" required className="percent-95-width">
          <label>{t("Clients.add_new_client.health_insurance_question")}</label>
        </FormControl>
      </Grid>
      <Grid item xs={1} className="answer">
        <FormControl margin="normal" required className="percent-95-width">
          <ImprovedSwitch
            color={"primary"}
            checked={state.client.hasHealthInsurance.value}
            onChange={(event) =>
              update(
                "client",
                "hasHealthInsurance",
                !state.client.hasHealthInsurance.value
              )
            }
            onLabel={""}
            offLabel={""}
          />
        </FormControl>
      </Grid>
      <Grid item xs={3} className="question">
        <FormControl margin="normal" required className="percent-95-width">
          <label>
            {t("Clients.add_new_client.disability_insurance_question")}
          </label>
        </FormControl>
      </Grid>
      <Grid item xs={1} className="answer">
        <FormControl margin="normal" required className="percent-95-width">
          <ImprovedSwitch
            color={"primary"}
            checked={state.client.hasDisabilityInsurance.value}
            onChange={(event) =>
              update(
                "client",
                "hasDisabilityInsurance",
                !state.client.hasDisabilityInsurance.value
              )
            }
            onLabel={""}
            offLabel={""}
          />
        </FormControl>
      </Grid>
      <Grid item xs={4}></Grid>
      <Grid item xs={3} className="question">
        <FormControl margin="normal" required className="percent-95-width">
          <label>{t("Clients.add_new_client.will_question")}</label>
        </FormControl>
      </Grid>
      <Grid item xs={1} className="answer">
        <FormControl margin="normal" required className="percent-95-width">
          <ImprovedSwitch
            color={"primary"}
            checked={state.client.hasWill.value}
            onChange={(event) =>
              update("client", "hasWill", !state.client.hasWill.value)
            }
            onLabel={""}
            offLabel={""}
          />
        </FormControl>
      </Grid>
      <Grid item xs={3} className="question">
        <FormControl margin="normal" required className="percent-95-width">
          <label>{t("Clients.add_new_client.trust_question")}</label>
        </FormControl>
      </Grid>
      <Grid item xs={1} className="answer">
        <FormControl margin="normal" required className="percent-95-width">
          <ImprovedSwitch
            color={"primary"}
            checked={state.client.hasTrust.value}
            onChange={(event) =>
              update("client", "hasTrust", !state.client.hasTrust.value)
            }
            onLabel={""}
            offLabel={""}
          />
        </FormControl>
      </Grid>
      <Grid item xs={4}></Grid>
      <Grid item xs={3} className="question">
        <FormControl margin="normal" required className="percent-95-width">
          <label>
            {t("Clients.add_new_client.beneficiaries_up_to_date_question")}
          </label>
        </FormControl>
      </Grid>
      <Grid item xs={1} className="answer">
        <FormControl margin="normal" required className="percent-95-width">
          <ImprovedSwitch
            color={"primary"}
            checked={state.client.isBeneficiariesUpToDate.value}
            onChange={(event) =>
              update(
                "client",
                "isBeneficiariesUpToDate",
                !state.client.isBeneficiariesUpToDate.value
              )
            }
            onLabel={""}
            offLabel={""}
          />
        </FormControl>
      </Grid>
      <Grid item xs={8}></Grid>
    </React.Fragment>
  );

  let content = (
    <React.Fragment>
      <Grid container spacing={3}>
        <Grid item xs={8}></Grid>
        <Grid
          item
          xs={4}
          className="buttons-group"
          style={{ textAlign: "right" }}
        >
          <div className={"client-wizard-buttons"}>{buttonsGroup}</div>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={3}
        className="add-new-client-modal-div"
        onScroll={hideDropdownOptions}
      >
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid className={"client-section-container"} item xs={12}>
              <Paper elevation={2} className={"client-section"}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography className={"client-form-section-header"} component={"p"} variant={"body1"} color={"primary"}>
                      {t("Clients.basic_information")}
                      <span className="required-condition">
                  {t("Clients.basic_information_required")}
                </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      {clientFirstNameComponent}
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <TextBox
                          placeholder={t("Clients.middle_name")}
                          minLength={0}
                          maxLength={30}
                          value={state.client.middleName.value}
                          onChange={(event) =>
                              update("client", "middleName", event.target.value)
                          }
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <TextBox
                          placeholder={t("Clients.last_name")}
                          minLength={0}
                          maxLength={30}
                          value={state.client.lastName.value}
                          onChange={(event) =>
                              update("client", "lastName", event.target.value)
                          }
                          validate={(value) => {
                            if (value === "") return "Last Name can't be empty.";
                          }}
                          hasError={!state.client.lastName.isValid}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <Typography component={"label"}>Gender</Typography>
                      <DropDownList
                          placeholder={t("Clients.gender")}
                          data={GENDER_DDL}
                          value={state.client.gender.value}
                          onChange={(event) =>
                              update("client", "gender", event.target.value)
                          }
                          reference={setOpenedDropdownList}
                      />
                    </FormControl>
                  </Grid>
                  {!props.isCoClient && (
                      <Grid item xs={4}>
                        <FormControl
                            margin="normal"
                            required
                            className="percent-95-width"
                        >
                          <Typography component={"label"}>Status</Typography>
                          <DropDownList
                              placeholder={t("Clients.status")}
                              data={ClientStatus.status}
                              textField={"value"}
                              dataItemKey={"id"}
                              value={state.client.status.value}
                              onChange={(event) =>
                                  update("client", "status", event.target.value)
                              }
                              reference={setOpenedDropdownList}
                          />
                        </FormControl>
                      </Grid>
                  )}
                  <Grid item xs={4} className="date-of-birth-div">
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width"
                    >
                      <NativeDatePicker
                          className={
                            state.client.dateOfBirth.value == null &&
                            !state.client.isNew
                                ? "MuiInput-root invalid MuiInput-underline Mui-error invalid"
                                : null
                          }
                          label={t("Clients.date_of_birth")}
                          selectedDate={
                            state.client.dateOfBirth.value
                                ? state.client.dateOfBirth.value
                                : null
                          }
                          maxDate={formatTimestampToDate(
                              new Date().getTime(),
                              "yyyy-MM-dd"
                          )}
                          onSelect={updateDateOfBirth("client", "dateOfBirth")}
                          hasError={
                            !(
                                state.client.dateOfBirth.isValid ||
                                !state.client.dateOfBirth.value
                            )
                          }
                          minDateMessage="Date of Birth cannot be before 1900"
                          maxDateMessage="Date of Birth cannot be after today"
                      />
                      {state.client.dateOfBirth.value == null &&
                          !state.client.isNew && (
                              <span className="invalid">
                      Date of Birth field can’t be empty
                    </span>
                          )}
                    </FormControl>
                  </Grid>
                  {props.isCoClient && (
                      <Grid item xs={4}>
                        &nbsp;
                      </Grid>
                  )}
                  {state.client.gender.value === "Other" && (
                      <React.Fragment>
                        <Grid item xs={4}>
                          <FormControl
                              margin="normal"
                              required
                              className="percent-95-width"
                          >
                            <TextBox
                                placeholder={"Other Gender"}
                                minLength={0}
                                maxLength={30}
                                value={state.client.genderOther.value}
                                onChange={(event) =>
                                    update("client", "genderOther", event.target.value)
                                }
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={8}></Grid>
                      </React.Fragment>
                  )}
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width client-retirement-age"
                    >
                      <Typography component={"label"}>Retirement Age</Typography>
                      <NumericalTextBox
                          placeholder={t("Clients.retirement_age")}
                          value={state.client.retirementAge.value}
                          min={1}
                          max={200}
                          step={1}
                          format="#"
                          hideArrows={true}
                          defaultValue={props.defaultRetirementAge}
                          valid={state.client.retirementAge.isValid}
                          update={(event) =>
                              update("client", "retirementAge", event.target.value)
                          }
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl
                        margin="normal"
                        required
                        className="percent-95-width client-life-expectancy"
                    >
                      <Typography component={"label"}>Life Expectancy</Typography>
                      <NumericalTextBox
                          placeholder={t("Clients.life_expectancy")}
                          value={state.client.lifeExpectancy.value}
                          min={1}
                          max={200}
                          step={1}
                          format="#"
                          hideArrows={true}
                          valid={state.client.lifeExpectancy.isValid}
                          defaultValue={props.defaultLifeExpectancyAge}
                          update={(event) =>
                              update("client", "lifeExpectancy", event.target.value)
                          }
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}></Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid className={"client-section-container"} item xs={12}>
              <Paper elevation={2} className={"client-section"}>
                <Grid container spacing={3}>
                  {setupContactInfoComponent("client")}
                </Grid>
              </Paper>
            </Grid>

            {!props.hideContactInformation &&
                !props.isCoClient && <Grid className={"client-section-container"} item xs={12}>
                  <Paper elevation={2} className={"client-section"}>
                    <Grid container spacing={3}>
                      {questionAnswerContent}
                    </Grid>
                  </Paper>
                </Grid>
              }
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );

  return (
    <div className="add-new-client-modal-container">
      <Typography variant={"h1"} component={"h1"}>
        {modalTitle}
      </Typography>
      {content}
    </div>
  );
};

export default AddNewClientForm;
